import React, { lazy, Suspense } from 'react';
import { BrowserRouter, Routes, Route, Navigate } from 'react-router-dom';
import { AuthProvider } from './context/AuthContext.jsx';
import AdminLayout from './components/AdminLayout.jsx';
import ProtectedRoute from './components/ProtectedRoute.jsx';
import './App.scss';

const LoginPage = lazy(() => import('./pages/LoginPage.jsx'));
const ForgotPassword = lazy(() => import('./pages/ForgotPassword.jsx'));
const Dashboard = lazy(() => import('./pages/Dashboard.jsx'));
const CompanyProfile = lazy(() => import('./pages/CompanyProfile.jsx'));
const EinvoiceSettings = lazy(() => import('./pages/EinvoiceSettings.jsx'));
const Integrations = lazy(() => import('./pages/Integrations.jsx'));
const AiAssistantSettings = lazy(() => import('./pages/AiAssistantSettings.jsx'));
const AccountingSettings = lazy(() => import('./pages/AccountingSettings.jsx'));
const MockErpConsole = lazy(() => import('./pages/MockErpConsole.jsx'));
const PrintTaxDocument = lazy(() => import('./print/PrintTaxDocument.jsx'));
const CustomerList = lazy(() => import('./pages/CustomerList.jsx'));
const CustomerInformation = lazy(() => import('./pages/CustomerInformation.jsx'));
const CustomerOrderHistory = lazy(() => import('./pages/CustomerOrderHistory.jsx'));
const SalesOrders = lazy(() => import('./pages/SalesOrders.jsx'));
const SalesOrderForm = lazy(() => import('./pages/SalesOrderForm.jsx'));
const SalesInvoices = lazy(() => import('./pages/SalesInvoices.jsx'));
const SalesInvoiceForm = lazy(() => import('./pages/SalesInvoiceForm.jsx'));
const CreditMemos = lazy(() => import('./pages/CreditMemos.jsx'));
const CreditMemoForm = lazy(() => import('./pages/CreditMemoForm.jsx'));
const Reports = lazy(() => import('./pages/Reports.jsx'));
const UserProfile = lazy(() => import('./pages/UserProfile.jsx'));
const UserManagement = lazy(() => import('./pages/UserManagement.jsx'));
const SkuDiscountList = lazy(() => import('./pages/SkuDiscountList.jsx'));
const DistributorList = lazy(() => import('./pages/DistributorList.jsx'));
const OrderStatusList = lazy(() => import('./pages/OrderStatusList.jsx'));
const SalesRepresentativeList = lazy(() => import('./pages/SalesRepresentativeList.jsx'));
const AuditTrail = lazy(() => import('./pages/AuditTrail.jsx'));
const StockLocationList = lazy(() => import('./pages/StockLocationList.jsx'));
const Inventory = lazy(() => import('./pages/Inventory.jsx'));
const FieldLayout = lazy(() => import('./field/FieldLayout.jsx'));
const FieldCustomers = lazy(() => import('./field/pages/FieldCustomers.jsx'));
const FieldCustomerCard = lazy(() => import('./field/pages/FieldCustomerCard.jsx'));
const FieldNewOrder = lazy(() => import('./field/pages/FieldNewOrder.jsx'));
const FieldOrders = lazy(() => import('./field/pages/FieldOrders.jsx'));
const FieldOrderDetail = lazy(() => import('./field/pages/FieldOrderDetail.jsx'));
const PortalLayout = lazy(() => import('./portal/PortalLayout.jsx'));
const PortalSignup = lazy(() => import('./portal/pages/PortalSignup.jsx'));
const PortalOrder = lazy(() => import('./portal/pages/PortalOrder.jsx'));
const PortalOrders = lazy(() => import('./portal/pages/PortalOrders.jsx'));
const PortalOrderDetail = lazy(() => import('./portal/pages/PortalOrderDetail.jsx'));
const PortalAccount = lazy(() => import('./portal/pages/PortalAccount.jsx'));
const PortalProjects = lazy(() => import('./portal/pages/PortalProjects.jsx'));
const PortalProject = lazy(() => import('./portal/pages/PortalProject.jsx'));
const CreditApprovals = lazy(() => import('./pages/CreditApprovals.jsx'));
const NotFound = lazy(() => import('./pages/NotFound.jsx'));

const Loader = () => (
  <div className="loading-overlay">
    <span className="material-icons">hourglass_empty</span> Loading…
  </div>
);

function App() {
  return (
    <AuthProvider>
      <BrowserRouter>
        <Suspense fallback={<Loader />}>
          <Routes>
            <Route path="/login" element={<LoginPage />} />
            <Route path="/forgot-password" element={<ForgotPassword />} />
            <Route path="/portal/signup" element={<PortalSignup />} />

            <Route element={<ProtectedRoute />}>
              <Route path="/print/sales-invoices/:id" element={<PrintTaxDocument kind="SI" />} />
              <Route path="/print/credit-memos/:id" element={<PrintTaxDocument kind="CM" />} />
              <Route path="/field" element={<FieldLayout />}>
                <Route index element={<FieldCustomers />} />
                <Route path="customers/:id" element={<FieldCustomerCard />} />
                <Route path="new" element={<FieldNewOrder />} />
                <Route path="new/:draftId" element={<FieldNewOrder />} />
                <Route path="orders" element={<FieldOrders />} />
                <Route path="orders/:id" element={<FieldOrderDetail />} />
              </Route>
              <Route path="/portal" element={<PortalLayout />}>
                <Route index element={<PortalOrder />} />
                <Route path="orders" element={<PortalOrders />} />
                <Route path="orders/:id" element={<PortalOrderDetail />} />
                <Route path="account" element={<PortalAccount />} />
                <Route path="projects" element={<PortalProjects />} />
                <Route path="projects/:id" element={<PortalProject />} />
              </Route>
              <Route element={<AdminLayout />}>
                <Route index element={<Navigate to="/dashboard" replace />} />
                <Route path="/dashboard" element={<Dashboard />} />

                <Route path="/customers" element={<CustomerList />} />
                <Route path="/customers/new" element={<CustomerInformation />} />
                <Route path="/customers/:id" element={<CustomerInformation />} />
                <Route path="/customers/:id/order-history" element={<CustomerOrderHistory />} />

                <Route path="/sales-orders" element={<SalesOrders />} />
                <Route path="/sales-orders/new" element={<SalesOrderForm />} />
                <Route path="/sales-orders/:id" element={<SalesOrderForm />} />

                <Route path="/sales-invoices" element={<SalesInvoices />} />
                <Route path="/sales-invoices/new" element={<SalesInvoiceForm />} />
                <Route path="/sales-invoices/:id" element={<SalesInvoiceForm />} />

                <Route path="/credit-memos" element={<CreditMemos />} />
                <Route path="/credit-memos/new" element={<CreditMemoForm />} />
                <Route path="/credit-memos/:id" element={<CreditMemoForm />} />

                <Route path="/inventory" element={<Inventory />} />
                <Route path="/credit-approvals" element={<CreditApprovals />} />
                <Route path="/reports" element={<Reports />} />

                <Route path="/settings/company-profile" element={<CompanyProfile />} />
                <Route path="/settings/einvoicing" element={<EinvoiceSettings />} />
                <Route path="/settings/integrations" element={<Integrations />} />
                <Route path="/settings/integrations/ai-assistant" element={<AiAssistantSettings />} />
                <Route path="/settings/integrations/accounting" element={<AccountingSettings />} />
                <Route path="/settings/integrations/mock-erp" element={<MockErpConsole />} />
                <Route path="/settings/profile" element={<UserProfile />} />
                <Route path="/settings/users" element={<UserManagement />} />
                <Route path="/settings/sku-discount" element={<SkuDiscountList />} />
                <Route path="/settings/distributors" element={<DistributorList />} />
                <Route path="/settings/order-status" element={<OrderStatusList />} />
                <Route path="/settings/stock-locations" element={<StockLocationList />} />
                <Route path="/settings/sales-representatives" element={<SalesRepresentativeList />} />
                <Route path="/settings/audit-trail" element={<AuditTrail />} />

                <Route path="*" element={<NotFound />} />
              </Route>
            </Route>
          </Routes>
        </Suspense>
      </BrowserRouter>
    </AuthProvider>
  );
}

export default App;
